import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {selectFinancialStatementById} from '../../../selectors/Ares/trialBalance';
import {getAnnotationsSliceById} from '../../../selectors/Ares/annotations';
import {AnnotationAction, maxLengthAnnotation} from '../../../util/uiconstants';
import {useEngagementId} from '../../../util/customHooks';
import {
	createEntityAnnotation,
	updateEntityAnnotation,
	deleteEntityAnnotation
} from '../../../actions/Annotation/annotationactions';
import Draggable from 'react-draggable';
import Icon from '../BaseComponentWrappers/MotifIcon';
import RichTextEditorWrapper from '../KendoEditor/RichTextEditorWrapper';
import {numberFormattedText, labels} from '../../../util/utils';
import DisablingSection from '../../Common/DisablingSection/DisablingSection';
import EllipsesControl from '../../Common/EllipsesControl/EllipsesControl';
import {
	IconoirNotes,
	IconoirXmark,
	IconoirCheck,
	IconoirDoubleCheck,
	IconoirInfoCircle,
	IconoirTrash
} from '@ey-xd/motif-icon';
import styled from 'styled-components';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';
import {createPortal} from 'react-dom';

const FinancialStatementAnnotation = ({userPreferences, isDisabled}, ref) => {
	const dispatch = useDispatch();

	const kendoRef = useRef(null);

	const [showLoader, setShowLoader] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [selectedFinancialStatementId, setSelectedFinancialStatementId] = useState(null);

	const [maxCharCountMessage, setMaxCharCountMessage] = useState('');
	const [maxCharCountExceeded, setMaxCharCountExceeded] = useState(false);

	const engagementId = useEngagementId();

	const selectedFinancialStatement = useSelector(selectFinancialStatementById(selectedFinancialStatementId));

	const annotationText = useSelector((state) => {
		const annotationId = (selectedFinancialStatement?.annotations || [])[0];

		if (annotationId) {
			return getAnnotationsSliceById(state, annotationId)?.annotationText;
		}

		return undefined;
	});

	const [editorValue, setEditorValue] = useState(annotationText);
	const [resetEditorKey, setResetEditorKey] = useState(0);

	const title = useMemo(() => {
		return selectedFinancialStatement?.financialStatementName;
	}, [selectedFinancialStatement]);

	const mode = useMemo(() => {
		return annotationText !== undefined ? AnnotationAction.edit : AnnotationAction.add;
	}, [annotationText]);

	const saveAnnotations = useCallback(
		(kendoText) => {
			setShowLoader(true);

			const annotation = {
				annotationText: kendoText,
				financialStatementId: selectedFinancialStatement?.id,
				currentUser: userPreferences.id
			};

			return dispatch(createEntityAnnotation(engagementId, annotation)).finally(() => setShowLoader(false));
		},
		[dispatch, engagementId, selectedFinancialStatement?.id, userPreferences?.id]
	);

	const editAnnotation = useCallback(
		(kendoText) => {
			if (isDisabled) {
				return;
			}

			setShowLoader(true);

			const annotation = {
				annotationId: (selectedFinancialStatement?.annotations ?? [])[0],
				annotationText: kendoText,
				financialStatementId: selectedFinancialStatement?.id,
				currentUser: userPreferences.id
			};

			return dispatch(updateEntityAnnotation(engagementId, annotation)).finally(() => setShowLoader(false));
		},
		[
			dispatch,
			engagementId,
			isDisabled,
			selectedFinancialStatement?.annotations,
			selectedFinancialStatement?.id,
			userPreferences?.id
		]
	);

	const deleteAnnotation = useCallback(() => {
		setShowLoader(true);

		const annotation = {
			annotationId: (selectedFinancialStatement?.annotations ?? [])[0],
			financialStatementId: selectedFinancialStatement?.id
		};

		return dispatch(deleteEntityAnnotation(engagementId, annotation)).finally(() => setShowLoader(false));
	}, [dispatch, engagementId, selectedFinancialStatement?.annotations, selectedFinancialStatement?.id]);

	const onChange = useCallback(
		(text) => {
			setEditorValue(text);

			if (text.length > maxLengthAnnotation) {
				setMaxCharCountExceeded(true);
				setMaxCharCountMessage(
					labels.maxLengthForEditResponseWithCount
						.replaceAll('{#}', numberFormattedText(userPreferences.numberFormat, text.length))
						.replaceAll('{##}', numberFormattedText(userPreferences.numberFormat, maxLengthAnnotation))
				);
			}
		},
		[userPreferences?.numberFormat]
	);

	const saveChanges = useCallback(
		(callback, evt) => {
			if (isDisabled) {
				evt.preventDefault();
				evt.stopPropagation();
				return false;
			}

			if (editorValue?.length > maxLengthAnnotation) {
				setMaxCharCountExceeded(true);
			} else {
				if (mode === AnnotationAction.add) {
					saveAnnotations && saveAnnotations(editorValue).then(() => callback && callback());
				} else {
					editAnnotation && editAnnotation(editorValue).then(() => callback && callback());
				}
			}
		},
		[mode, saveAnnotations, editAnnotation, editorValue]
	);

	const handleDeleteAnnotation = useCallback(
		(evt) => {
			if (isDisabled) {
				evt.preventDefault();
				evt.stopPropagation();

				return false;
			}
			return deleteAnnotation().then(() => hideModal());
		},
		[isDisabled, hideModal, deleteAnnotation]
	);

	const hideModal = useCallback(
		(event) => {
			if (event) {
				event?.stopPropagation();
				event?.preventDefault();
			}

			setSelectedFinancialStatementId(null);
			setMaxCharCountMessage('');
			setMaxCharCountExceeded(false);
			setEditorValue(annotationText);
			setShowModal(false);
		},
		[annotationText]
	);

	const deleteOption = useMemo(() => {
		return (
			<section className="delete-link" onClick={handleDeleteAnnotation} title={labels.deleteNote}>
				<Icon src={IconoirTrash} />
				{labels.deleteNote}
			</section>
		);
	}, [hideModal, deleteAnnotation]);

	const titleTooltip = useMemo(
		() => (title ? title + ' - ' + labels.OARAnnotationModalTitle : labels.OARAnnotationModalTitle),
		[title]
	);

	useEffect(() => {
		setEditorValue(annotationText);
		setResetEditorKey((prev) => prev + 1);
	}, [selectedFinancialStatementId]);

	useImperativeHandle(
		ref,
		() => ({
			setShowModal,
			showModal,
			setSelectedFinancialStatementId
		}),
		[setShowModal, showModal, setSelectedFinancialStatementId]
	);

	const editorJsx = (
		<RichTextEditorWrapper
			key={resetEditorKey}
			uniqueId={`financial${resetEditorKey}`}
			characterCountPrefix={labels.characters}
			charactersLabel={labels.characters}
			maxCharCount={maxLengthAnnotation}
			onChange={onChange}
			ofLabel={labels.of}
			ref={kendoRef}
			showCharacterCount
			showMaxCharacterCount
			useGuidedWorkFlowToolset
			useMinimalToolset={false}
			value={editorValue || annotationText}
			resize={false}
			placeholder={labels.commentText}
			addCustomScrollbar
		/>
	);

	const component = (
		<Draggable disabled>
			<StyledDraggableSection className="StyledDraggableSection StyledDraggableSectionAnnotation">
				<LoadingIndicator show={showLoader} />
				<section className="DraggableHeaderContainer">
					<section className="NotesIcon">
						<Icon src={IconoirNotes} title={titleTooltip} size="medium" />
					</section>
					<section className="HeaderLabel" id="annotationsDivHeader">
						{title && (
							<>
								<EllipsesControl noOfLines={1} content={title} tooltip={title} />
								<section className="HypenSeparater">{' - '}</section>
							</>
						)}
						<section className="ModalTitleLabel">{labels.OARAnnotationModalTitle}</section>
					</section>
					<section className="CloseIcon">
						<Icon title={labels.closeLabel} src={IconoirXmark} onClick={hideModal} />
					</section>
				</section>
				<section className={`AnnotationWrappers-Ares`}>
					<section className="ModalPlaceholderText">{labels.OARAnnotationModalPlaceholder}</section>
					{editorJsx}
					<DisablingSection isDisabled={isDisabled}>
						<section className="save-container">
							<section
								className="save-link"
								onClick={(evt) => {
									saveChanges(hideModal, evt);
								}}
								title={labels.saveAndCloseButtonTitle}
							>
								<Icon src={IconoirDoubleCheck} />
								{labels.saveAndCloseButtonTitle}
							</section>
							<section className="save-link" onClick={(evt) => saveChanges(undefined, evt)} title={labels.save}>
								<Icon src={IconoirCheck} />
								{labels.save}
							</section>
							<DisablingSection disabled={mode === AnnotationAction.add || isDisabled}>{deleteOption}</DisablingSection>
							<section className="cancel-link" onClick={hideModal} title={labels.cancelLabel}>
								<Icon src={IconoirXmark} />
								{labels.cancelLabel}
							</section>
						</section>
					</DisablingSection>
					{maxCharCountExceeded && (
						<section className="max-count-exceeded">
							<Icon src={IconoirInfoCircle} />
							<span className="MaxCharCountMessageText">{maxCharCountMessage}</span>
						</section>
					)}
				</section>
			</StyledDraggableSection>
		</Draggable>
	);

	if (showModal) {
		return createPortal(component, document.body);
	}

	return null;
};

export default forwardRef(FinancialStatementAnnotation);

const StyledDraggableSection = styled.section`
	position: fixed;
	top: 25%;
	left: 30%;
	width: var(--px-750);
	background-color: var(--neutrals-00white);
	border-radius: var(--px-12);
	box-shadow: var(--px-2) var(--px-2) var(--px-7) 0 rgba(0, 0, 0, 0.75);
	z-index: 9999999999;
	.motif-progress-loader {
		width: 100%;
		height: 100%;
		position: absolute;
		z-index: 9999999999;
		display: flex;
		justify-content: center;
		align-items: center;
	}
	.ql-editor {
		max-height: 10rem;
		overflow: auto;
		li[data-list='bullet'] > .ql-ui:before {
			font-family: auto;
		}
	}
	.ql-editor .motif-progress-loader {
		position: absolute;
		display: flex;
		width: 100%;
		height: 100%;
		justify-content: center;
		align-items: center;
		z-index: 1;
	}
	.DraggableHeaderContainer {
		display: flex;
		align-items: center;
		padding: var(--px-10) var(--px-24) var(--px-10) var(--px-24);
		cursor: default;
		background-color: var(--neutrals-900);
		color: var(--neutrals-00white);
		height: var(--px-40);
		border-top-left-radius: var(--px-12);
		border-top-right-radius: var(--px-12);
		.NotesIcon {
			display: flex;
			padding-right: var(--px-10);
			.motif-icon {
				display: flex;
				svg {
					width: var(--px-20) !important;
					height: var(--px-20) !important;
					color: var(--neutrals-00white);
					fill: none !important;
				}
			}
		}
		.HeaderLabel {
			display: inline-flex;
			align-items: center;
			font-size: var(--px-16) !important;
			font-weight: 600 !important;
			width: calc(100% - var(--px-80));
			.ellipses {
				display: inline-flex;
				width: auto;
				max-width: 60%;
			}
			.HypenSeparater {
				display: inline-flex;
				margin: 0 var(--px-2);
			}
			.ModalTitleLabel {
				display: inline-flex;
				min-width: auto;
			}
		}
		.CloseIcon {
			margin-left: auto;
			.motif-icon {
				display: flex;
				svg {
					width: var(--px-20) !important;
					height: var(--px-20) !important;
					color: var(--neutrals-00white);
					cursor: pointer;
				}
			}
		}
	}
	.AnnotationWrappers-Ares {
		display: flex;
		flex-direction: column;
		padding: var(--px-20) var(--px-24) var(--px-30) var(--px-24);
		font-size: var(--px-14);
		min-height: var(--px-400);
		.ModalPlaceholderText {
			font-size: var(--px-16);
			font-weight: 300;
			line-height: var(--px-24);
			margin-bottom: var(--px-10);
		}
		.form-group {
			&.rich-text-kendo-editor {
				display: flex;
				flex-direction: column;
				margin-bottom: 0;
				.kendo-editor-label {
					.pull-right {
						font-size: var(--px-12);
						font-weight: 300;
					}
				}
				.k-editor {
					height: var(--px-300);
					background-color: var(--neutrals-00white);
					border: var(--px-1) solid var(--neutrals-200);
					border-bottom: 0;
					.k-toolbar {
						padding: var(--px-18) var(--px-16);
						border: 0;
						border-bottom: var(--px-1) solid var(--neutrals-200);
						box-shadow: none;
						background-color: var(--neutrals-00white);
						.k-button-group {
							.k-dropdownlist {
								width: var(--px-158);
								background-color: var(--neutrals-00white);
								border: var(--px-1) solid var(--neutrals-200) !important;
								color: var(--neutrals-900);
								.k-input-inner {
									font-size: var(--px-14) !important;
									font-weight: 300 !important;
									line-height: var(--px-22) !important;
								}
								.k-button {
									border: 0 !important;
									border-left: var(--px-1) solid var(--neutrals-200) !important;
								}
							}
							.k-picker {
								background-color: var(--neutrals-00white);
								border: var(--px-1) solid var(--neutrals-200) !important;
								color: var(--neutrals-900);
								border-radius: var(--px-8) !important;
								.k-button {
									border: 0 !important;
								}
							}
							.k-button {
								background-color: var(--neutrals-00white);
								border: var(--px-1) solid var(--neutrals-200) !important;
								color: var(--neutrals-900);
								border-radius: var(--px-8) !important;
								& + .k-button {
									border-radius: 0 !important;
								}
								&.k-group-start {
									border-radius: var(--px-8) 0 0 var(--px-8) !important;
								}
								&.k-group-end {
									border-radius: 0 var(--px-8) var(--px-8) 0 !important;
								}
							}
						}
					}
					.k-editor-content {
						padding: var(--px-16);
					}
				}
				.k-widget {
					background-color: var(--neutrals-00white);
					border: var(--px-1) solid var(--neutrals-200);
					border-radius: var(--px-12) var(--px-12) 0 0;
					border-bottom: 0;
					.k-widget {
						padding: var(--px-12) var(--px-16);
						border: 0;
						border-bottom: var(--px-1) solid var(--neutrals-200);
						box-shadow: none;
						.k-button-group {
							.k-dropdownlist {
								width: var(--px-158);
								background-color: var(--neutrals-00white);
								border: var(--px-1) solid var(--neutrals-200) !important;
								color: var(--neutrals-900);
								.k-input-inner {
									font-size: var(--px-14) !important;
									font-weight: 300 !important;
									line-height: var(--px-22) !important;
								}
								.k-button {
									border: 0 !important;
									border-left: var(--px-1) solid var(--neutrals-200) !important;
								}
							}
							.k-picker {
								background-color: var(--neutrals-00white);
								border: var(--px-1) solid var(--neutrals-200) !important;
								color: var(--neutrals-900);
								border-radius: var(--px-8) !important;
								.k-button {
									border: 0 !important;
								}
							}
							.k-button {
								background-color: var(--neutrals-00white);
								border: var(--px-1) solid var(--neutrals-200) !important;
								color: var(--neutrals-900);
								border-radius: var(--px-8) !important;
								& + .k-button {
									border-radius: 0 !important;
								}
								&.k-group-start {
									border-radius: var(--px-8) 0 0 var(--px-8) !important;
								}
								&.k-group-end {
									border-radius: 0 var(--px-8) var(--px-8) 0 !important;
								}
							}
						}
					}
					.k-editor-content {
						padding: var(--px-4) var(--px-8);
					}
				}
			}
		}
		.save-container {
			display: inline-flex;
			justify-content: flex-end;
			font-weight: bold;
			color: var(--neutrals-1000);
			border: var(--px-1) solid var(--neutrals-200);
			border-top: 0;
			border-radius: 0 0 var(--px-12) var(--px-12);
			padding: var(--px-10) var(--px-10) var(--px-10) 0;
			background-color: var(--neutrals-00white);
			.save-link {
				display: flex;
				align-items: center;
				padding-right: var(--px-15);
				color: var(--blue-600);
				cursor: pointer;
				.motif-icon {
					display: flex;
					align-items: center;
				}
				svg {
					color: var(--blue-600);
					margin-right: var(--px-5);
					width: var(--px-20) !important;
					height: var(--px-20) !important;
				}
			}
			.StyledDisablingSection {
				margin: 0 !important;
			}
			.delete-link {
				display: flex;
				align-items: center;
				margin-right: var(--px-15);
				color: var(--blue-600);
				cursor: pointer;
				.motif-icon {
					display: flex;
					align-items: center;
					height: var(--px-24) !important;
					margin-right: var(--px-5);
					svg {
						color: var(--blue-600);
						fill: none !important;
						width: var(--px-20) !important;
						height: var(--px-20) !important;
						margin-top: var(--px-2);
					}
				}
				&.disabled {
				}
			}
			.cancel-link {
				display: flex;
				align-items: center;
				color: var(--blue-600);
				cursor: pointer;
				.motif-icon {
					display: flex;
					align-items: center;
				}
				svg {
					color: var(--blue-600);
					fill: none !important;
					margin-right: var(--px-5);
					width: var(--px-20) !important;
					height: var(--px-20) !important;
				}
			}
		}
		.max-count-exceeded {
			position: relative;
			top: var(--px-5);
			display: flex;
			align-items: flex-start;
			line-height: var(--px-20);
			font-size: var(--px-12);
			font-weight: 300;
			color: var(--red-600);
			.motif-icon {
				margin-right: var(--px-5);
				svg {
					width: var(--px-20) !important;
					height: var(--px-20) !important;
					color: var(--red-600);
					fill: none !important;
					path {
						color: var(--red-600);
						fill: none !important;
					}
				}
			}
		}
	}
`;
